import React, { useEffect, useMemo, useRef, useState } from "react";
import SIZE_SYSTEMS from "./sizeSystems";
import COLOR_PALETTE from "./colorPalette";
import {
  listCategories,
  createProduct,
  updateProduct,
  uploadCatalogImage,
} from "./productsService";
import "./adminCatalogAdditions.css";

// A "variant" is one color option (or a single implicit variant if the
// product has no color options at all). Each variant either has one
// priceId/price (no sizes) or one priceId/price PER size (has sizes).
function buildEmptyVariant(color) {
  return {
    colorKey: color ? color.label.toLowerCase().replace(/\s+/g, "-") : "default",
    label: color ? color.label : "",
    swatch: color ? color.swatch : null,
    imageFile: null,
    imageUrl: null,
    priceId: "",
    price: "",
    active: true,
    sizePrices: {}, // { [sizeLabel]: { priceId, price, stockQty, hidden } }
  };
}

// An image option is the same variant record as a color option — it just
// identifies itself by a picture instead of a swatch. Reusing `variants`
// rather than inventing a parallel array means pricing, per-size pricing,
// stock, hiding, the cart and every past order keep working unchanged; the
// storefront only has to draw the chooser differently.
let imageOptionSeq = 0;
function buildImageVariant(file) {
  imageOptionSeq += 1;
  return {
    ...buildEmptyVariant(null),
    colorKey: `image-${Date.now()}-${imageOptionSeq}`,
    // A sensible starting name — the filename minus its extension. It's the
    // tooltip shoppers see on the thumbnail, so it's editable below.
    label: (file?.name || "Option").replace(/\.[^.]+$/, "").replace(/[_-]+/g, " "),
    swatch: null,
    imageFile: file || null,
    previewUrl: file ? URL.createObjectURL(file) : null,
    optionType: "image",
  };
}

// ---------------------------------------------------------------------------
// Imported products store their per-size pricing as an ARRAY:
//
//   sizes: [{ label: "S", priceId: "price_...", price: 24.99 }, ...]
//
// The editor works on a MAP keyed by size label (`sizePrices`). Nothing used
// to convert between the two, so opening an imported product and pressing
// Save read `v.sizePrices["S"]` — undefined — and wrote back priceId: "" and
// price: 0 for every size of every color. That silently destroyed the Stripe
// price IDs on the product and the Buy button stopped working, with no error
// anywhere. This function is the fix; do not remove it.
// ---------------------------------------------------------------------------
function hydrateVariant(v) {
  const sizePrices = { ...(v.sizePrices || {}) };

  if (Array.isArray(v.sizes)) {
    v.sizes.forEach((s) => {
      if (!s?.label) return;
      // An existing sizePrices entry wins — it means this product has already
      // been through the editor and the map is the newer of the two.
      if (sizePrices[s.label]) return;
      sizePrices[s.label] = {
        priceId: s.priceId || "",
        price: s.price ?? "",
        stockQty: s.stockQty ?? "",
        hidden: s.hidden === true,
      };
    });
  }

  return {
    ...buildEmptyVariant(null),
    ...v,
    active: v.active !== false,
    imageUrl: v.imageUrl || v.image || null,
    sizePrices,
  };
}

// Sizes a variant actually carries, in the order the size system lists them
// rather than the order they were clicked.
function orderSizes(sizes, systemKey) {
  const order = SIZE_SYSTEMS[systemKey]?.sizes || [];
  if (!order.length) return sizes;
  return [...sizes].sort((a, b) => {
    const ai = order.indexOf(a);
    const bi = order.indexOf(b);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });
}

export default function AddProductForm({ editingProduct, onSaved, onCancel }) {
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState(editingProduct?.categoryId || "");
  const [name, setName] = useState(editingProduct?.name || "");
  const [description, setDescription] = useState(editingProduct?.description || "");

  // Whether the product shows in the storefront at all. ProductsPage already
  // filters on `active !== false`; until now nothing could set it to false.
  const [active, setActive] = useState(editingProduct?.active !== false);

  const [hasSizes, setHasSizes] = useState(editingProduct?.hasSizes || false);
  const [sizeSystemKey, setSizeSystemKey] = useState(editingProduct?.sizeSystem || "clothing");
  const [selectedSizes, setSelectedSizes] = useState(editingProduct?.selectedSizes || []);
  const [customSizeInput, setCustomSizeInput] = useState("");

  // Image options and color options are two answers to the same question —
  // "what varies about this product?" — so only one can be on at a time.
  // Letting both run would give a product two independent swatch rows built
  // from one flat variants array, and neither would be right.
  const [hasImageOptions, setHasImageOptions] = useState(
    editingProduct?.hasImageOptions || false
  );

  const [hasColors, setHasColors] = useState(
    editingProduct?.hasImageOptions ? false : editingProduct?.hasColors || false
  );
  const [selectedColorLabels, setSelectedColorLabels] = useState(
    editingProduct?.variants?.filter((v) => v.colorKey !== "default").map((v) => v.label) || []
  );
  const [customColorLabel, setCustomColorLabel] = useState("");
  const [customColorHex, setCustomColorHex] = useState("#000000");

  // Swatches for colors that aren't in COLOR_PALETTE — imported products use
  // labels like "TAGGG Classic Tee – Blue", which no palette entry matches.
  // Keeping them here stops every custom color from inheriting whatever the
  // color picker happened to be showing at the time.
  const [customSwatches, setCustomSwatches] = useState(() => {
    const map = {};
    (editingProduct?.variants || []).forEach((v) => {
      if (v.label && !COLOR_PALETTE.some((c) => c.label === v.label)) {
        map[v.label] = v.swatch || "#000000";
      }
    });
    return map;
  });

  const [variants, setVariants] = useState(
    editingProduct?.variants?.length
      ? editingProduct.variants.map(hydrateVariant)
      : [buildEmptyVariant(null)]
  );
  const [mainImageFile, setMainImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Read by the unmount cleanup below. A ref, not the state value, because the
  // cleanup runs once with whatever it closed over — and that would be the
  // empty list from first render.
  const variantsRef = useRef(variants);
  variantsRef.current = variants;

  useEffect(() => {
    listCategories().then(setCategories);
  }, []);

  // Rebuild the variants list whenever color selection changes, preserving
  // any pricing already entered for colors that are still selected.
  useEffect(() => {
    // Image options own the variants array outright — they're added and
    // removed one file at a time, not derived from a palette. Without this
    // guard the branch below would flatten every image option back to a
    // single blank variant on the next render.
    if (hasImageOptions) return;

    if (!hasColors) {
      setVariants((prev) => [{ ...buildEmptyVariant(null), ...prev[0] }]);
      return;
    }
    const allColors = [
      ...COLOR_PALETTE.filter((c) => selectedColorLabels.includes(c.label)),
      ...selectedColorLabels
        .filter((label) => !COLOR_PALETTE.some((c) => c.label === label))
        .map((label) => ({ label, swatch: customSwatches[label] || "#000000" })),
    ];
    setVariants((prev) =>
      allColors.map((color) => {
        const existing = prev.find((v) => v.label === color.label);
        return existing || buildEmptyVariant(color);
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasColors, hasImageOptions, selectedColorLabels, customSwatches]);

  // The sizes this product actually comes in. It is `selectedSizes`, not the
  // whole size system — an imported tee that comes in S–XXL was previously
  // re-saved with XS and XXXL rows bolted on, because this returned the full
  // clothing list and every save wrote it back.
  const availableSizes = useMemo(() => {
    if (!hasSizes) return [];
    if (selectedSizes.length) return orderSizes(selectedSizes, sizeSystemKey);
    if (sizeSystemKey === "custom") return [];
    return SIZE_SYSTEMS[sizeSystemKey]?.sizes || [];
  }, [hasSizes, sizeSystemKey, selectedSizes]);

  const toggleSize = (size) => {
    setSelectedSizes((prev) =>
      prev.includes(size) ? prev.filter((s) => s !== size) : [...prev, size]
    );
  };

  const addCustomSize = () => {
    const trimmed = customSizeInput.trim();
    if (!trimmed || selectedSizes.includes(trimmed)) return;
    setSelectedSizes((prev) => [...prev, trimmed]);
    setCustomSizeInput("");
  };

  // Drop a size from the product entirely, across every color.
  const removeSize = (size) => {
    if (
      !window.confirm(
        `Remove size "${size}" from every color of this product? Its price IDs and stock counts go with it.`
      )
    ) {
      return;
    }
    setSelectedSizes((prev) => prev.filter((s) => s !== size));
    setVariants((prev) =>
      prev.map((v) => {
        const next = { ...(v.sizePrices || {}) };
        delete next[size];
        return { ...v, sizePrices: next };
      })
    );
  };

  // ---------------------------------------------------------------------
  // Image options
  // ---------------------------------------------------------------------

  // Object URLs are held by the browser until they're revoked. Leaving them
  // behind on a form the admin opens and closes all afternoon leaks the whole
  // image each time.
  useEffect(() => {
    return () => {
      variantsRef.current.forEach((v) => {
        if (v.previewUrl) URL.revokeObjectURL(v.previewUrl);
      });
    };
  }, []);

  const toggleImageOptions = (checked) => {
    setHasImageOptions(checked);
    if (checked) {
      setHasColors(false);
      // Anything already on the product becomes the first image option, so a
      // plain product with one photo doesn't lose its pricing when it's
      // promoted to a multi-image one.
      setVariants((prev) =>
        prev.some((v) => v.imageUrl || v.imageFile || v.previewUrl)
          ? prev.map((v) => ({ ...v, optionType: "image", swatch: null }))
          : []
      );
    } else {
      // Falls back through the effect above, which collapses to a single
      // variant and keeps the first one's pricing.
      setVariants((prev) => [{ ...buildEmptyVariant(null), ...prev[0] }]);
    }
  };

  const addImageOptions = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setVariants((prev) => [...prev, ...files.map(buildImageVariant)]);
  };

  const replaceVariantImage = (index, file) => {
    if (!file) return;
    setVariants((prev) => {
      const next = [...prev];
      const current = next[index];
      if (current.previewUrl) URL.revokeObjectURL(current.previewUrl);
      next[index] = {
        ...current,
        imageFile: file,
        previewUrl: URL.createObjectURL(file),
      };
      return next;
    });
  };

  const moveImageOption = (index, delta) => {
    const target = index + delta;
    setVariants((prev) => {
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const toggleColor = (label) => {
    setSelectedColorLabels((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
    );
  };

  const addCustomColor = () => {
    const trimmed = customColorLabel.trim();
    if (!trimmed || selectedColorLabels.includes(trimmed)) return;
    setCustomSwatches((prev) => ({ ...prev, [trimmed]: customColorHex }));
    setSelectedColorLabels((prev) => [...prev, trimmed]);
    setCustomColorLabel("");
  };

  // ---------------------------------------------------------------------
  // Removing one color from a product.
  //
  // Clicking a palette swatch off only ever worked for colors that exist in
  // COLOR_PALETTE. An imported variant is labelled "TAGGG Classic Tee – Blue",
  // which matches no swatch, so there was no control anywhere that could take
  // it off the product. This is that control.
  //
  // It has to drop the label from selectedColorLabels as well as the variant
  // itself — the effect above rebuilds `variants` from that list, so removing
  // only the variant would put it straight back on the next render.
  // ---------------------------------------------------------------------
  const removeVariant = (index) => {
    const v = variants[index];
    const what = hasImageOptions ? "this image" : "this color";
    if (
      !window.confirm(
        `Remove "${v.label || what}" from ${name || "this product"}? ` +
          `It stops being offered as soon as you save.`
      )
    ) {
      return;
    }
    if (v.previewUrl) URL.revokeObjectURL(v.previewUrl);
    setSelectedColorLabels((prev) => prev.filter((l) => l !== v.label));
    setVariants((prev) => prev.filter((_, i) => i !== index));
  };

  const updateVariantField = (index, field, value) => {
    setVariants((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const updateVariantSizePrice = (index, size, field, value) => {
    setVariants((prev) => {
      const next = [...prev];
      const sizePrices = { ...next[index].sizePrices };
      sizePrices[size] = { ...sizePrices[size], [field]: value };
      next[index] = { ...next[index], sizePrices };
      return next;
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Product name is required.");
      return;
    }
    if (hasColors && variants.length === 0) {
      setError("A product with color options needs at least one color.");
      return;
    }
    if (hasImageOptions && variants.length === 0) {
      setError("Add at least one image, or untick “This product has image options”.");
      return;
    }
    if (hasImageOptions && variants.some((v) => !v.imageFile && !v.imageUrl)) {
      setError("Every image option needs a picture. Add one, or remove that option.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const mainImageUrl = mainImageFile
        ? await uploadCatalogImage(mainImageFile, "product-images")
        : editingProduct?.image || null;

      const finalVariants = await Promise.all(
        variants.map(async (v) => {
          const imageUrl = v.imageFile
            ? await uploadCatalogImage(v.imageFile, "product-images")
            : v.imageUrl || mainImageUrl;

          // Shared by both branches. `optionType` is what the storefront reads
          // to decide between a color dot and a thumbnail; `swatch` is null on
          // an image option so nothing downstream paints a stray dot.
          const identity = {
            colorKey: v.colorKey,
            label: v.label?.trim() || name,
            swatch: hasImageOptions ? null : v.swatch,
            optionType: hasImageOptions ? "image" : "color",
            image: imageUrl,
            active: v.active !== false,
          };

          if (hasSizes) {
            const sizes = availableSizes.map((size) => ({
              label: size,
              priceId: v.sizePrices?.[size]?.priceId || "",
              price: Number.parseFloat(v.sizePrices?.[size]?.price) || 0,
              stockQty: Number.parseInt(v.sizePrices?.[size]?.stockQty, 10) || 0,
              hidden: v.sizePrices?.[size]?.hidden === true,
            }));
            return { ...identity, sizes };
          }

          return {
            ...identity,
            priceId: v.priceId,
            price: Number.parseFloat(v.price) || 0,
            stockQty: Number.parseInt(v.stockQty, 10) || 0,
          };
        })
      );

      const productData = {
        name: name.trim(),
        description: description.trim(),
        categoryId: categoryId || null,
        active,
        hasSizes,
        // Written explicitly as false rather than left off, so a product that
        // used to have colors and is now image-based stops rendering swatches.
        hasColors: hasImageOptions ? false : hasColors,
        hasImageOptions,
        sizeSystem: hasSizes ? sizeSystemKey : null,
        selectedSizes: hasSizes ? availableSizes : [],
        // A product with image options has no separate "main" photo to pick —
        // the first option is the one the card leads with.
        image: hasImageOptions
          ? finalVariants[0]?.image || mainImageUrl
          : mainImageUrl,
        variants: finalVariants,
      };

      if (editingProduct?.id) {
        await updateProduct(editingProduct.id, productData);
      } else {
        await createProduct(productData);
      }

      onSaved?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const isImported = Boolean(editingProduct?.importedFrom);

  return (
    <form onSubmit={handleSave} className="admin-panel admin-product-form">
      {/* The editor opens above a catalog list that can be screens long, so the
          only way back used to be the Cancel button at the very bottom. This
          header stays put while the form scrolls. */}
      <div className="admin-form-header">
        <h3>
          {editingProduct ? "Edit Product" : "Add Product"}
          {editingProduct?.name && (
            <span className="admin-form-header-name">{editingProduct.name}</span>
          )}
        </h3>
        {onCancel && (
          <button
            type="button"
            className="admin-form-close"
            onClick={onCancel}
            title="Close without saving and go back to the catalog"
          >
            × Close editor
          </button>
        )}
      </div>

      {isImported && (
        <p className="admin-hint admin-imported-note">
          Imported from the static TAGGG file. It behaves like any other
          catalog product from here — colors and sizes can be removed, hidden
          or repriced, and the static file is never read again.
        </p>
      )}

      <label>Category</label>
      <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
        <option value="">— Select a category —</option>
        {categories.map((cat) => (
          <option key={cat.id} value={cat.id}>{cat.name}</option>
        ))}
      </select>

      <label>Product Name</label>
      <input type="text" value={name} onChange={(e) => setName(e.target.value)} />

      <label>Description</label>
      <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />

      {/* One image, or many. A product with image options gets its pictures
          from the options themselves — a separate "main" image there is just
          a second answer to the same question. */}
      {!hasImageOptions && (
        <>
          <label>
            Main Image{" "}
            {hasColors && "(used as fallback if a color has no image of its own)"}
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setMainImageFile(e.target.files?.[0] || null)}
          />
          {editingProduct?.image && !mainImageFile && (
            <div className="admin-current-image">
              <img src={editingProduct.image} alt={editingProduct.name || "Current"} />
              <span className="admin-hint">Current image — choose a file to replace it.</span>
            </div>
          )}
        </>
      )}

      {/* Hiding beats deleting when a product might come back — the order
          history keeps pointing at a document that still exists. */}
      <div className="admin-toggle-row admin-toggle-row--visibility">
        <label>
          <input
            type="checkbox"
            checked={!active}
            onChange={(e) => setActive(!e.target.checked)}
          />
          Hide this product from the store
        </label>
        {!active && (
          <span className="admin-badge admin-badge--hidden">
            Hidden — nobody can see or buy it
          </span>
        )}
      </div>

      <div className="admin-toggle-row">
        <label>
          <input type="checkbox" checked={hasSizes} onChange={(e) => setHasSizes(e.target.checked)} />
          This product has size options
        </label>
      </div>

      {hasSizes && (
        <div className="admin-subsection">
          <label>Size System</label>
          <select value={sizeSystemKey} onChange={(e) => setSizeSystemKey(e.target.value)}>
            {Object.entries(SIZE_SYSTEMS).map(([key, system]) => (
              <option key={key} value={key}>{system.label}</option>
            ))}
          </select>

          {sizeSystemKey === "custom" ? (
            <div className="admin-custom-add">
              <input
                type="text"
                placeholder="Type a size and press Add"
                value={customSizeInput}
                onChange={(e) => setCustomSizeInput(e.target.value)}
              />
              <button type="button" onClick={addCustomSize}>Add</button>
            </div>
          ) : (
            <div className="admin-checkbox-grid">
              {SIZE_SYSTEMS[sizeSystemKey].sizes.map((size) => (
                <label key={size} className="admin-checkbox-pill">
                  <input
                    type="checkbox"
                    checked={selectedSizes.includes(size)}
                    onChange={() => toggleSize(size)}
                  />
                  {size}
                </label>
              ))}
            </div>
          )}

          {availableSizes.length > 0 && (
            <div className="admin-selected-sizes">
              <span className="admin-hint">In these sizes:</span>
              {availableSizes.map((size) => (
                <span className="admin-size-tag" key={size}>
                  {size}
                  <button
                    type="button"
                    className="admin-tag-x"
                    title={`Remove ${size} from this product`}
                    onClick={() => removeSize(size)}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="admin-toggle-row">
        <label>
          <input
            type="checkbox"
            checked={hasImageOptions}
            onChange={(e) => toggleImageOptions(e.target.checked)}
          />
          This product has image options
        </label>
        {hasColors && !hasImageOptions && (
          <span className="admin-hint admin-hint--inline">
            Turning this on replaces the color options below.
          </span>
        )}
      </div>

      {hasImageOptions && (
        <div className="admin-subsection">
          <label>Images</label>
          <p className="admin-hint">
            Each image becomes a swatch on the product card — shoppers pick
            between them the way they'd pick a color. Every image gets its own
            price and stock count in the Pricing section below.
          </p>

          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => {
              addImageOptions(e.target.files);
              // Clearing the input lets the same file be picked again after a
              // removal — without it the browser fires no change event the
              // second time and the click looks broken.
              e.target.value = "";
            }}
          />

          {variants.length === 0 ? (
            <p className="admin-hint admin-hint--muted">
              No images yet. Add one or more above — you can pick several at once.
            </p>
          ) : (
            <div className="admin-image-options">
              {variants.map((v, i) => {
                const src = v.previewUrl || v.imageUrl;
                return (
                  <div
                    className={`admin-image-option ${
                      v.active === false ? "is-hidden-variant" : ""
                    }`}
                    key={v.colorKey || i}
                  >
                    <div className="admin-image-option-thumb">
                      {src ? (
                        <img src={src} alt={v.label || `Option ${i + 1}`} />
                      ) : (
                        <span className="admin-image-option-empty">No image</span>
                      )}
                    </div>

                    <div className="admin-image-option-body">
                      <input
                        type="text"
                        className="admin-image-option-label"
                        value={v.label || ""}
                        placeholder={`Option ${i + 1} name`}
                        title="Shown when a shopper hovers the swatch"
                        onChange={(e) => updateVariantField(i, "label", e.target.value)}
                      />

                      <div className="admin-image-option-controls">
                        <label className="admin-image-option-replace">
                          Replace image
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              replaceVariantImage(i, e.target.files?.[0]);
                              e.target.value = "";
                            }}
                          />
                        </label>

                        <label className="admin-variant-hide">
                          <input
                            type="checkbox"
                            checked={v.active === false}
                            onChange={(e) =>
                              updateVariantField(i, "active", !e.target.checked)
                            }
                          />
                          Hide
                        </label>

                        <button
                          type="button"
                          className="admin-image-option-move"
                          onClick={() => moveImageOption(i, -1)}
                          disabled={i === 0}
                          title="Move earlier — the first image is the one the card leads with"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="admin-image-option-move"
                          onClick={() => moveImageOption(i, 1)}
                          disabled={i === variants.length - 1}
                          title="Move later"
                        >
                          ↓
                        </button>

                        <button
                          type="button"
                          className="admin-variant-remove"
                          onClick={() => removeVariant(i)}
                          title={`Remove ${v.label || "this image"} from this product`}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {!hasImageOptions && (
        <div className="admin-toggle-row">
          <label>
            <input
              type="checkbox"
              checked={hasColors}
              onChange={(e) => setHasColors(e.target.checked)}
            />
            This product has color options
          </label>
        </div>
      )}

      {hasColors && (
        <div className="admin-subsection">
          <label>Choose colors</label>
          <div className="admin-color-grid">
            {COLOR_PALETTE.map((c) => (
              <button
                type="button"
                key={c.label}
                className={`admin-color-swatch ${selectedColorLabels.includes(c.label) ? "selected" : ""}`}
                style={{ backgroundColor: c.swatch }}
                title={c.label}
                onClick={() => toggleColor(c.label)}
              />
            ))}
          </div>
          <div className="admin-custom-add">
            <input
              type="text"
              placeholder="Custom color name"
              value={customColorLabel}
              onChange={(e) => setCustomColorLabel(e.target.value)}
            />
            <input
              type="color"
              value={customColorHex}
              onChange={(e) => setCustomColorHex(e.target.value)}
            />
            <button type="button" onClick={addCustomColor}>Add Custom Color</button>
          </div>
          <p className="admin-hint">
            Colors that aren't in the palette above — including every imported
            one — are removed with the Remove button on their pricing block.
          </p>
        </div>
      )}

      {/* Pricing per variant */}
      <div className="admin-subsection">
        <label>Pricing</label>
        {variants.length === 0 && (
          <p className="admin-hint">
            {hasImageOptions
              ? "No images yet. Add one above, or untick “This product has image options” to sell it as a single item."
              : "No colors left. Add one above, or untick “This product has color options” to sell it as a single item."}
          </p>
        )}
        {variants.map((v, i) => (
          <div
            className={`admin-variant-block ${v.active === false ? "is-hidden-variant" : ""}`}
            key={v.colorKey || v.label || i}
          >
            {/* Image options get a read-only header here — the picture, the
                name and the remove button all live in the Images section
                above, and duplicating them would give two controls for the
                same thing. */}
            {hasImageOptions && (
              <div className="admin-variant-header admin-variant-header--image">
                <span className="admin-variant-thumb">
                  {(v.previewUrl || v.imageUrl) ? (
                    <img src={v.previewUrl || v.imageUrl} alt={v.label || `Option ${i + 1}`} />
                  ) : null}
                </span>
                <strong>{v.label || `Option ${i + 1}`}</strong>
                {v.active === false && (
                  <span className="admin-badge admin-badge--hidden">Hidden</span>
                )}
              </div>
            )}

            {hasColors && (
              <div className="admin-variant-header">
                <span className="admin-variant-swatch" style={{ backgroundColor: v.swatch }} />
                <strong>{v.label}</strong>

                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => updateVariantField(i, "imageFile", e.target.files?.[0] || null)}
                />

                <label className="admin-variant-hide">
                  <input
                    type="checkbox"
                    checked={v.active === false}
                    onChange={(e) => updateVariantField(i, "active", !e.target.checked)}
                  />
                  Hide this color
                </label>

                <button
                  type="button"
                  className="admin-variant-remove"
                  onClick={() => removeVariant(i)}
                  title={`Remove ${v.label} from this product`}
                >
                  Remove color
                </button>
              </div>
            )}

            {v.active === false && (
              <p className="admin-hint admin-hint--muted">
                Hidden. The swatch and its sizes stay on the record — untick to
                bring it back — but nobody sees or buys it.
              </p>
            )}

            {hasSizes ? (
              <div className="admin-size-price-grid">
                {availableSizes.map((size) => (
                  <div className="admin-size-price-row admin-size-price-row--sized" key={size}>
                    <span>{size}</span>
                    <input
                      type="text"
                      placeholder="price_..."
                      value={v.sizePrices?.[size]?.priceId || ""}
                      onChange={(e) => updateVariantSizePrice(i, size, "priceId", e.target.value)}
                    />
                    <input
                      type="number"
                      step="0.01"
                      placeholder="$"
                      value={v.sizePrices?.[size]?.price ?? ""}
                      onChange={(e) => updateVariantSizePrice(i, size, "price", e.target.value)}
                    />
                    <input
                      type="number"
                      step="1"
                      min="0"
                      placeholder="In stock"
                      value={v.sizePrices?.[size]?.stockQty ?? ""}
                      onChange={(e) => updateVariantSizePrice(i, size, "stockQty", e.target.value)}
                    />
                    <label className="admin-size-hide" title={`Hide ${v.label} in ${size}`}>
                      <input
                        type="checkbox"
                        checked={v.sizePrices?.[size]?.hidden === true}
                        onChange={(e) =>
                          updateVariantSizePrice(i, size, "hidden", e.target.checked)
                        }
                      />
                      Hide
                    </label>
                  </div>
                ))}
              </div>
            ) : (
              <div className="admin-size-price-row admin-size-price-row--simple">
                <input
                  type="text"
                  placeholder="price_... (Stripe Price ID)"
                  value={v.priceId}
                  onChange={(e) => updateVariantField(i, "priceId", e.target.value)}
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="$"
                  value={v.price}
                  onChange={(e) => updateVariantField(i, "price", e.target.value)}
                />
                <input
                  type="number"
                  step="1"
                  min="0"
                  placeholder="In stock"
                  value={v.stockQty ?? ""}
                  onChange={(e) => updateVariantField(i, "stockQty", e.target.value)}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {error && <p className="admin-error">{error}</p>}

      <div className="admin-form-actions">
        <button type="submit" disabled={saving}>
          {saving ? "Saving…" : editingProduct ? "Save Changes" : "Add Product"}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel}>
            Close without saving
          </button>
        )}
      </div>
    </form>
  );
}
